<template>
  <ECard
    as="article"
    class="entity"
    :elevation="0"
    :data-track-name="title"
    :data-track-type-hints="typeHints.join()"
    overflow-visible
  >
    <EMapHeader
      v-if="map && map.placeName"
      v-bind="commonProps"
      :gallery="gallery"
      class="entity__header"
      :map="map"
    />
    <EGalleryHeader
      v-else-if="gallery.length > 0"
      v-bind="commonProps"
      class="entity__header"
      :gallery="gallery"
      :more-images-url="moreImagesUrl"
      :prefer-image-source-url="preferImageSourceUrl"
      @image-click="$emit('gallery-image-click')"
    />
    <EBaseHeader
      v-else
      v-bind="commonProps"
      class="entity__header"
      @image-click="$emit('main-image-click')"
    />
    <div class="entity__content">
      <slot name="content-start" />
      <p
        v-if="description"
        class="entity__description"
        data-test-id="entity-description"
      >
        {{ truncatedDescription }}
        <ELink
          v-if="isDescriptionTruncated"
          :href="moreUrl"
          color="result"
          :target="linkTarget"
          class="entity__more-link"
          data-test-id="entity-more-link"
          @click="$emit('read-more-click')"
        >
          {{ $t('web.entities.readMore') }}
        </ELink>
      </p>
      <ELinks
        :links="linksWithWebsite"
        class="entity__links"
        data-test-id="entity-links"
        @link-click="(link) => $emit('link-click', link)"
      />
      <slot name="content-end" />
    </div>
    <slot name="footer">
      <ERichContentFooter
        as="footer"
        :sources="sources"
        :feedback-link="feedbackLinkProps"
        :powered-by-ecosia="poweredByEcosia"
        class="entity-footer"
        data-test-id="entity-footer"
      >
        <span v-if="license">
          {{ $t('web.entities.license.text') }}
          <ELink
            target="_blank"
            :href="license.url"
            color="secondary"
            rel="noopener"
            data-test-id="entity-license-link"
          >
            {{ license.title }}
          </ELink>
        </span>
      </ERichContentFooter>
    </slot>
  </ECard>
</template>

<script>
import { mapGetters } from 'vuex';
import { truncateText } from '~~/common/js/universal/utils';
import ECard from '~~/common/vue/components/card';
import ELink from '~~/common/vue/components/link';
import ERichContentFooter, { validateSource } from '~~/common/vue/components/rich-content-footer';
import ETitles from '../header/titles';
import EBaseHeader from '../header/base';
import EGalleryHeader from '../header/gallery';
import EMapHeader from '../header/map';
import ELinks from './links';

export const DESCRIPTION_TRUNCATION_LENGTH = 320;

const isValidLocation = ({ latitude, longitude, placeName }) => typeof latitude === 'number' &&
  typeof longitude === 'number' &&
  typeof placeName === 'string';

export default {
  name: 'EBaseEntity',
  components: {
    ECard,
    ELink,
    ELinks,
    ERichContentFooter,
    EBaseHeader,
    EGalleryHeader,
    EMapHeader,
  },
  props: {
    url: {
      type: String,
      default: '',
    },
    title: ETitles.props.title,
    subtitle: ETitles.props.subtitle,
    image: EBaseHeader.props.image,
    imageUrl: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    moreUrl: {
      type: String,
      default: '',
    },
    links: ELinks.props.links,
    sources: ERichContentFooter.props.sources,
    gallery: {
      type: Array,
      default: () => [],
    },
    typeHints: {
      type: Array,
      default: () => [],
    },
    license: {
      type: [Object, null],
      validator: (license) => !license || validateSource(license),
      default: null,
    },
    map: {
      type: Object,
      default: null,
      validator: isValidLocation,
    },
    ticketSubject: {
      type: String,
      default: 'Entity',
    },
    moreImagesUrl: EGalleryHeader.props.moreImagesUrl,
    preferImageSourceUrl: EGalleryHeader.props.preferImageSourceUrl,
    entityTypes: {
      type: Array,
      default: () => [],
    },
    widgetType: {
      type: String,
      default: 'entity',
    },
    poweredByEcosia: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('user', ['linkTarget']),
    isDescriptionTruncated() {
      return this.description.length > DESCRIPTION_TRUNCATION_LENGTH;
    },
    truncatedDescription() {
      return truncateText(this.description, DESCRIPTION_TRUNCATION_LENGTH);
    },
    commonProps() {
      const { title, subtitle, image } = this.$props;
      return { title, subtitle, image, imageUrl: this.imageUrl };
    },
    feedbackLinkProps() {
      return {
        errorMessage: this.$t('common.header.feedback.error'),
        options: [
          { label: this.$t('common.header.feedback.bullet1'), value: 'helpful' },
          { label: this.$t('common.header.feedback.bullet2'), value: 'missing' },
          { label: this.$t('common.header.feedback.bullet3'), value: 'wrong' },
          { label: this.$t('common.header.feedback.bullet4'), value: 'not_helpful' },
        ],
        query: this.$route.query.q?.toString(),
        submitText: this.$t('common.header.feedback.send'),
        successMessage: this.$t('common.header.feedback.body2'),
        successTitle: this.$t('common.header.feedback.title2'),
        textareaPlaceholder: this.$t('common.header.feedback.tellmore'),
        title: this.$t('common.header.feedback.login'),
        ticketSubject: `Search - ${this.ticketSubject}`,
        url: this.$config.feedbackUrl,
        widgetType: this.widgetType,
      };
    },
    linksWithWebsite() {
      if (this.url) {
        const websiteLink = {
          title: this.$t('web.entities.officialwebsite'),
          url: this.url,
        };
        return [websiteLink, ...this.links];
      }
      return this.links;
    },
  },
};
</script>

<style lang="scss" scoped>
.entity {
  @include desktop {
    border: solid $border-width var(--color-decorative-border-1);
  }
}

.entity__header {
  overflow: hidden;

  @include desktop {
    border-top-left-radius: $border-radius-l;
    border-top-right-radius: $border-radius-l;
  }
}

.entity__content {
  padding: $entity-base-vertical-padding $entity-base-horizontal-padding;

  @include desktop {
    padding: $entity-desktop-padding;
  }
}

.entity__description {
  margin: 0;

  ~ .entity__links {
    padding-top: $space-m;
  }
}

.entity__more-link {
  white-space: nowrap;
}

.entity-footer {
  // aligns horizontal padding overriding the ones set in SourcesFooter
  @include desktop-down {
    padding-left: $entity-base-horizontal-padding;
    padding-right: $entity-base-horizontal-padding;
  }
}

@include desktop {
  .web__mainline .entity {
    display: none;
  }
}
</style>
